import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Package, Plus, RefreshCw, Truck, CheckCircle2, FileText,
  AlertTriangle, ClipboardList, Signature, Printer,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/utils-format";
import { GrnLabelPrintDialog } from "@/components/GrnLabelPrintDialog";

/**
 * GRNPage — Goods Received Note inbox for showroom staff.
 * Shows inbound dispatches → click "Create GRN" → enter received qty → save draft
 * → confirm receive to auto add-in to local stock.
 */
export default function GRNPage() {
  const { user } = useAuth();
  const [inbound, setInbound] = useState([]);
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("inbound"); // "inbound" | "grns"
  const [createFrom, setCreateFrom] = useState(null); // dispatch object to create GRN from
  const [createBlank, setCreateBlank] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [transferSearch, setTransferSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [inRes, grRes] = await Promise.all([
        api.get("/grn/inbound"),
        api.get("/grn"),
      ]);
      setInbound(inRes.data || []);
      setGrns(grRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load GRNs");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => {
    const drafts = grns.filter((g) => g.status === "draft").length;
    const received = grns.filter((g) => g.status === "received").length;
    return { inbound: inbound.length, drafts, received };
  }, [inbound, grns]);

  return (
    <div className="space-y-6" data-testid="grn-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-[Outfit] text-2xl font-semibold text-[#2C1810] flex items-center gap-2">
            <Package className="w-6 h-6 text-[#C05621]" />
            Goods Received Notes
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Receive stock arriving from the warehouse. Marking a GRN as{" "}
            <span className="font-medium text-emerald-700">Received</span> automatically
            adds the quantities to your local stock.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="grn-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={() => setCreateBlank(true)}
            className="bg-[#C05621] hover:bg-[#9C4221] text-white"
            data-testid="grn-create-blank-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New Blank GRN
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3">
        <KpiTile label="Pending Inbound" value={kpis.inbound} icon={Truck} tone="blue" testid="grn-kpi-inbound" />
        <KpiTile label="Draft GRNs" value={kpis.drafts} icon={ClipboardList} tone="amber" testid="grn-kpi-drafts" />
        <KpiTile label="Received (total)" value={kpis.received} icon={CheckCircle2} tone="emerald" testid="grn-kpi-received" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#EAE5D9]">
        <TabBtn active={tab === "inbound"} onClick={() => setTab("inbound")} testid="grn-tab-inbound">
          Inbound dispatches ({inbound.length})
        </TabBtn>
        <TabBtn active={tab === "grns"} onClick={() => setTab("grns")} testid="grn-tab-grns">
          All GRNs ({grns.length})
        </TabBtn>
      </div>

      {tab === "inbound" ? (
        <>
          <div className="max-w-sm">
            <Input
              value={transferSearch}
              onChange={(e) => setTransferSearch(e.target.value)}
              placeholder="Type or scan the Transfer ID (e.g. BMF/DSP/26/0001)…"
              data-testid="grn-transfer-id-search"
            />
          </div>
          <InboundTable
            rows={inbound.filter((d) => (d.number || "").toLowerCase().includes(transferSearch.trim().toLowerCase()))}
            loading={loading}
            onCreateFrom={(d) => setCreateFrom(d)}
          />
        </>
      ) : (
        <GRNTable rows={grns} loading={loading} onView={(g) => setDetailId(g.id)} />
      )}

      {createFrom && (
        <GRNCreateDialog
          dispatch={createFrom}
          onClose={() => setCreateFrom(null)}
          onCreated={(g) => { setCreateFrom(null); setDetailId(g.id); load(); }}
        />
      )}
      {createBlank && (
        <GRNCreateDialog
          blank
          onClose={() => setCreateBlank(false)}
          onCreated={(g) => { setCreateBlank(false); setDetailId(g.id); load(); }}
        />
      )}
      {detailId && (
        <GRNDetailDialog
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm border-b-2 transition ${
        active
          ? "border-[#C05621] text-[#C05621] font-medium"
          : "border-transparent text-[#8B7355] hover:text-[#2C1810]"
      }`}
    >
      {children}
    </button>
  );
}

function KpiTile({ label, value, icon: Icon, tone, testid }) {
  const bg = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
  }[tone] || "bg-white border-[#EAE5D9]";
  return (
    <div className={`border rounded-lg p-3 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function InboundTable({ rows, loading, onCreateFrom }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left uc-label border-b border-[#EAE5D9]">
            <th className="px-3 py-2">Dispatch #</th>
            <th className="px-3 py-2">From</th>
            <th className="px-3 py-2">Driver / Vehicle</th>
            <th className="px-3 py-2">E-way</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Dispatched</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8B7355]">Loading…</td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8B7355]">
              No inbound dispatches. When warehouse sends stock, it will appear here.
            </td></tr>
          )}
          {!loading && rows.map((d) => (
            <tr key={d.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]"
              data-testid={`grn-inbound-row-${d.id}`}>
              <td className="px-3 py-2 font-mono text-[12px] font-medium">{d.number}</td>
              <td className="px-3 py-2">
                <div className="font-medium text-[#2C1810]">{d.from_branch_code}</div>
                <div className="text-[11px] text-[#8B7355]">{d.from_branch_name}</div>
              </td>
              <td className="px-3 py-2 text-[12px]">
                {d.driver_name || "—"}
                <div className="text-[11px] text-[#8B7355]">{d.vehicle_no || "no vehicle"}</div>
              </td>
              <td className="px-3 py-2 text-[12px] font-mono">{d.eway_no || "—"}</td>
              <td className="px-3 py-2">
                <Badge className="border bg-blue-50 border-blue-200 text-blue-800">{d.current_status}</Badge>
              </td>
              <td className="px-3 py-2 text-[11px] text-[#8B7355]">{fmtDate(d.created_at)}</td>
              <td className="px-3 py-2 text-right">
                <Button
                  size="sm"
                  onClick={() => onCreateFrom(d)}
                  data-testid={`grn-create-from-${d.id}`}
                  className="bg-[#C05621] hover:bg-[#9C4221] text-white"
                >
                  <Plus className="w-3 h-3 mr-1" /> Create GRN
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GRNTable({ rows, loading, onView }) {
  return (
    <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left uc-label border-b border-[#EAE5D9]">
            <th className="px-3 py-2">GRN #</th>
            <th className="px-3 py-2">Dispatch</th>
            <th className="px-3 py-2">From</th>
            <th className="px-3 py-2">Items</th>
            <th className="px-3 py-2">Variance</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Created</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={8} className="px-3 py-8 text-center text-[#8B7355]">Loading…</td></tr>
          )}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={8} className="px-3 py-8 text-center text-[#8B7355]">No GRNs yet.</td></tr>
          )}
          {!loading && rows.map((g) => (
            <tr key={g.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]"
              data-testid={`grn-row-${g.id}`}>
              <td className="px-3 py-2 font-mono text-[12px] font-medium">{g.number}</td>
              <td className="px-3 py-2 text-[12px] font-mono">{g.dispatch_id ? "Linked" : "—"}</td>
              <td className="px-3 py-2 text-[12px]">
                <div className="font-medium">{g.from_branch_code}</div>
                <div className="text-[11px] text-[#8B7355]">{g.from_branch_name}</div>
              </td>
              <td className="px-3 py-2 text-[12px] text-center">{g.items_count}</td>
              <td className={`px-3 py-2 text-[12px] font-mono ${Math.abs(g.variance_total || 0) > 0 ? "text-red-600" : "text-emerald-700"}`}>
                {g.variance_total > 0 ? "+" : ""}{g.variance_total || 0}
              </td>
              <td className="px-3 py-2">
                {g.status === "received" ? (
                  <Badge className="border bg-emerald-100 border-emerald-200 text-emerald-800">Received</Badge>
                ) : (
                  <Badge className="border bg-amber-100 border-amber-200 text-amber-800">Draft</Badge>
                )}
              </td>
              <td className="px-3 py-2 text-[11px] text-[#8B7355]">{fmtDate(g.created_at)}</td>
              <td className="px-3 py-2 text-right">
                <Button size="sm" variant="outline" onClick={() => onView(g)} data-testid={`grn-view-${g.id}`}>View</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Create GRN Dialog ----------
function GRNCreateDialog({ dispatch, blank, onClose, onCreated }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [invoiceRef, setInvoiceRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const [fromBranch, setFromBranch] = useState("");

  useEffect(() => {
    (async () => {
      if (blank) {
        try {
          const { data } = await api.get(`/branches`);
          setBranches(data || []);
        } catch { /* ignore */ }
      }
      if (dispatch) {
        setFromBranch(dispatch.from_branch_id);
        // Prefill from dispatch._prefill_items (attached by /inbound endpoint)
        const pf = dispatch._prefill_items || [];
        if (pf.length) {
          setItems(pf.map((it) => ({
            product_id: it.product_id,
            name: it.name || "",
            dispatched_qty: Number(it.quantity || 0),
            received_qty: Number(it.quantity || 0),
            unit: it.unit || "pcs",
            note: "",
            discrepancy_reason: "",
            batches: it.batches || [],
            unit_cost: it.unit_cost ?? null,
          })));
        } else {
          setItems([blankItem()]);
        }
      } else {
        setItems([blankItem()]);
      }
    })();
    // eslint-disable-next-line
  }, [dispatch?.id, blank]);

  function blankItem() {
    return { product_id: "", name: "", dispatched_qty: 0, received_qty: 0, unit: "pcs", note: "", discrepancy_reason: "" };
  }

  const addRow = () => setItems((its) => [...its, blankItem()]);
  const removeRow = (i) => setItems((its) => its.filter((_, idx) => idx !== i));
  const setRow = (i, patch) => setItems((its) => its.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const submit = async () => {
    if (blank && !fromBranch) { toast.error("Pick a source branch"); return; }
    const cleanItems = items.filter((it) => it.product_id && it.received_qty >= 0);
    if (cleanItems.length === 0) { toast.error("Add at least one item with a product and received qty"); return; }
    const unexplained = cleanItems.filter((it) => Number(it.received_qty || 0) !== Number(it.dispatched_qty || 0) && !(it.discrepancy_reason || "").trim());
    if (unexplained.length) {
      toast.error(`Add a discrepancy reason for: ${unexplained.map((it) => it.name).join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        dispatch_id: dispatch?.id,
        from_branch_id: dispatch?.from_branch_id || fromBranch,
        to_branch_id: user.branch_id,
        items: cleanItems.map((it) => ({
          product_id: it.product_id,
          name: it.name,
          dispatched_qty: Number(it.dispatched_qty || 0),
          received_qty: Number(it.received_qty),
          unit: it.unit,
          note: it.note,
          discrepancy_reason: it.discrepancy_reason,
          batches: it.batches || undefined,
          unit_cost: it.unit_cost ?? undefined,
        })),
        notes,
        invoice_ref: invoiceRef,
      };
      const { data } = await api.post("/grn", payload);
      toast.success(`GRN ${data.number} created (draft) — review and receive to update stock`);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create GRN");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="grn-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Package className="w-5 h-5 text-[#C05621]" />
            {dispatch ? `Create GRN — from ${dispatch.number}` : "Create Blank GRN"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            {dispatch
              ? `Received from ${dispatch.from_branch_name}. Enter actual received quantities — any variance from dispatched will be recorded.`
              : "For stock received without a linked dispatch (e.g. direct supplier delivery)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {blank && (
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">From branch</Label>
              <Select value={fromBranch} onValueChange={setFromBranch}>
                <SelectTrigger data-testid="grn-from-branch"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Invoice / DN reference</Label>
              <Input value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} placeholder="e.g. DN/2026/0087" data-testid="grn-invoice-ref" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-[#8B7355]">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. 1 damaged carton" data-testid="grn-notes" />
            </div>
          </div>

          <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uc-label border-b border-[#EAE5D9] bg-[#FDFBF7]">
                  <th className="px-2 py-2">Product</th>
                  <th className="px-2 py-2 w-20 text-right">Sent</th>
                  <th className="px-2 py-2 w-24 text-right">Received *</th>
                  <th className="px-2 py-2 w-16">Unit</th>
                  <th className="px-2 py-2 w-20 text-right">Variance</th>
                  {!!dispatch && <th className="px-2 py-2 w-24 text-right text-[#C05621]">HO Supply ₹</th>}
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const variance = Number(it.received_qty || 0) - Number(it.dispatched_qty || 0);
                  const hasDiscrepancy = Math.abs(variance) > 0;
                  return (
                    <React.Fragment key={i}>
                    <tr className={hasDiscrepancy ? "border-b-0 bg-red-50/40" : "border-b border-[#EAE5D9] last:border-0"} data-testid={`grn-item-row-${i}`}>
                      <td className="px-2 py-1.5">
                        <Input
                          value={it.name}
                          onChange={(e) => setRow(i, { name: e.target.value })}
                          placeholder="Product name"
                          className="h-8 text-sm"
                          data-testid={`grn-item-name-${i}`}
                        />
                        <ProductQuickPicker
                          onPick={(p) => setRow(i, { product_id: p.id, name: p.name, unit: p.unit || "pcs" })}
                          currentName={it.name}
                          testid={`grn-product-picker-${i}`}
                        />
                        {!it.product_id && (
                          <div className="text-[10px] text-red-600 mt-0.5">Pick a product from suggestions</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Input
                          type="number"
                          value={it.dispatched_qty}
                          onChange={(e) => setRow(i, { dispatched_qty: e.target.value })}
                          className="h-8 text-sm text-right"
                          disabled={!!dispatch}
                          data-testid={`grn-item-sent-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Input
                          type="number"
                          value={it.received_qty}
                          onChange={(e) => setRow(i, { received_qty: e.target.value })}
                          className="h-8 text-sm text-right"
                          data-testid={`grn-item-received-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input value={it.unit} onChange={(e) => setRow(i, { unit: e.target.value })}
                          className="h-8 text-sm" data-testid={`grn-item-unit-${i}`} />
                      </td>
                      <td className={`px-2 py-1.5 text-right text-sm font-mono ${Math.abs(variance) > 0 ? "text-red-600" : "text-emerald-700"}`}>
                        {variance > 0 ? "+" : ""}{variance}
                      </td>
                      {!!dispatch && (
                        <td className="px-2 py-1.5 text-right text-xs text-[#C05621]" data-testid={`grn-item-ho-rate-${i}`}>
                          {Number(it.unit_cost) > 0 ? `₹${Number(it.unit_cost).toFixed(2)}` : <span className="text-[#8B7355]">—</span>}
                        </td>
                      )}
                      <td className="px-2 py-1.5">
                        {items.length > 1 && (
                          <button
                            onClick={() => removeRow(i)}
                            data-testid={`grn-item-remove-${i}`}
                            className="text-[#8B7355] hover:text-red-600"
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                    {hasDiscrepancy && (
                      <tr className="bg-red-50/40 border-b border-red-200" data-testid={`grn-discrepancy-row-${i}`}>
                        <td colSpan={dispatch ? 7 : 6} className="px-2 pb-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                            <Input
                              value={it.discrepancy_reason}
                              onChange={(e) => setRow(i, { discrepancy_reason: e.target.value })}
                              placeholder="Required: reason for discrepancy (e.g. Damaged in transit, Short shipment)"
                              className={`h-8 text-xs ${!it.discrepancy_reason?.trim() ? "border-red-400" : "border-emerald-400"}`}
                              data-testid={`grn-discrepancy-reason-${i}`}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button variant="outline" size="sm" onClick={addRow} data-testid="grn-item-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="grn-create-submit" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
            {busy ? "Creating…" : "Save GRN (draft)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Product quick-picker (autocomplete) ----------
function ProductQuickPicker({ onPick, currentName, testid = "grn-product-picker" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?search=${encodeURIComponent(q)}`);
        setSuggestions((data || []).slice(0, 6));
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="mt-1">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search product to link…"
        className="h-7 text-[11px]"
        data-testid={testid}
      />
      {suggestions.length > 0 && (
        <div className="border border-[#EAE5D9] rounded mt-1 max-h-32 overflow-y-auto bg-white shadow-sm" data-testid={`${testid}-results`}>
          {suggestions.map((p) => (
            <button
              key={p.id}
              onClick={() => { onPick(p); setSuggestions([]); setQ(""); }}
              data-testid={`${testid}-option-${p.id}`}
              className="w-full text-left px-2 py-1 text-[11px] hover:bg-[#FDFBF7] border-b border-[#EAE5D9] last:border-0"
            >
              <span className="font-medium">{p.name}</span> <span className="text-[#8B7355]">({p.sku})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- GRN Detail Dialog ----------
function GRNDetailDialog({ id, onClose, onChanged }) {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";
  const [grn, setGrn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signName, setSignName] = useState("");
  const [notes, setNotes] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editQtys, setEditQtys] = useState({});
  const [labelsOpen, setLabelsOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/grn/${id}`);
      setGrn(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load GRN");
      onClose();
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const receive = async () => {
    if (!window.confirm("Confirm receipt? This will add the received quantities to your local stock.")) return;
    setBusy(true);
    try {
      await api.post(`/grn/${id}/receive`, {
        notes: notes || undefined,
        signature_name: signName || undefined,
      });
      toast.success("GRN received — stock updated");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to receive");
    } finally { setBusy(false); }
  };

  const startEdit = () => {
    const q = {};
    (grn.items || []).forEach((it) => { q[it.product_id] = it.received_qty; });
    setEditQtys(q);
    setEditMode(true);
  };

  const saveCorrection = async () => {
    setBusy(true);
    try {
      const items = (grn.items || []).map((it) => ({ ...it, received_qty: Number(editQtys[it.product_id]) }));
      await api.patch(`/grn/${id}`, { items });
      toast.success("Correction saved — showroom stock adjusted");
      setEditMode(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save correction");
    } finally { setBusy(false); }
  };

  const deleteGrn = async () => {
    if (!window.confirm(`Delete ${grn.number}? This reverses the showroom stock this GRN added.`)) return;
    setBusy(true);
    try {
      await api.delete(`/grn/${id}`);
      toast.success(`${grn.number} deleted — stock reversed`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally { setBusy(false); }
  };

  if (!grn) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="bg-white max-w-md">
          <div className="py-6 text-center text-[#8B7355]">Loading GRN…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const isDraft = grn.status === "draft";
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="grn-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#C05621]" />
            {grn.number}
            {grn.status === "received"
              ? <Badge className="ml-2 border bg-emerald-100 border-emerald-200 text-emerald-800">Received</Badge>
              : <Badge className="ml-2 border bg-amber-100 border-amber-200 text-amber-800">Draft</Badge>}
            {grn.has_discrepancy && (
              <Badge className="ml-1 border-0 bg-red-600 text-white" data-testid="grn-detail-discrepancy-badge">
                <AlertTriangle className="w-3 h-3 mr-1" /> Discrepancy Flagged
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">
            {grn.from_branch_name} → {grn.to_branch_name}
            {grn.invoice_ref && <> · Invoice/DN: <span className="font-mono">{grn.invoice_ref}</span></>}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9] bg-[#FDFBF7]">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Received</th>
                <th className="px-3 py-2 text-right">Variance</th>
                <th className="px-3 py-2">Unit</th>
                <th className="px-3 py-2">Note / Discrepancy Reason</th>
              </tr>
            </thead>
            <tbody>
              {(grn.items || []).map((it, i) => {
                const v = Number(it.received_qty || 0) - Number(it.dispatched_qty || 0);
                return (
                  <tr key={i} className={`border-b border-[#EAE5D9] last:border-0 ${Math.abs(v) > 0 ? "bg-red-50/40" : ""}`}>
                    <td className="px-3 py-2">{it.name}</td>
                    <td className="px-3 py-2 text-right font-mono">{it.dispatched_qty}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">
                      {editMode ? (
                        <Input type="number" value={editQtys[it.product_id]} onChange={(e) => setEditQtys({ ...editQtys, [it.product_id]: e.target.value })}
                          className="h-7 w-20 text-right ml-auto" data-testid={`grn-edit-qty-${it.product_id}`} />
                      ) : it.received_qty}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${Math.abs(v) > 0 ? "text-red-600" : "text-emerald-700"}`}>
                      {v > 0 ? "+" : ""}{v}
                    </td>
                    <td className="px-3 py-2 text-[12px]">{it.unit}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {Math.abs(v) > 0
                        ? <span className="text-red-700 font-medium">{it.discrepancy_reason || "—"}</span>
                        : <span className="text-[#8B7355]">{it.note || "—"}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {grn.status === "received" && grn.stock_updates && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-[12px]">
            <div className="flex items-center gap-1.5 font-medium text-emerald-800 mb-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Stock added-in
            </div>
            <ul className="text-emerald-900 space-y-0.5">
              {grn.stock_updates.map((s, i) => {
                const it = (grn.items || []).find((x) => x.product_id === s.product_id);
                return (
                  <li key={i}>
                    +{s.delta} of {it?.name || s.product_id} → local qty now <b>{s.new_qty}</b>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {isDraft && !editMode && (
          <div className="border-t border-[#EAE5D9] pt-3 space-y-2">
            <Label className="text-[10px] uppercase text-[#8B7355]">Optional</Label>
            <Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Your name (signature)" data-testid="grn-sign-name" />
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes on receipt" data-testid="grn-receive-notes" />
            <div className="flex justify-end">
              <Button onClick={receive} disabled={busy} data-testid="grn-receive-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Signature className="w-4 h-4 mr-1" />
                {busy ? "Receiving…" : "Confirm Receipt & Add to Stock"}
              </Button>
            </div>
          </div>
        )}

        {isSuperAdmin && (
          <div className="border-t border-[#EAE5D9] pt-3 flex items-center justify-between">
            <div className="text-[11px] text-[#8B7355]">HO Data Governance — Super Admin only.</div>
            <div className="flex gap-2">
              {editMode ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                  <Button size="sm" onClick={saveCorrection} disabled={busy} data-testid="grn-save-correction-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                    {busy ? "Saving…" : "Save Correction"}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="outline" onClick={startEdit} data-testid="grn-edit-btn">Edit Qty</Button>
                  <Button size="sm" variant="outline" onClick={deleteGrn} disabled={busy} data-testid="grn-delete-btn" className="border-red-300 text-red-700 hover:bg-red-50">
                    {busy ? "Deleting…" : "Delete GRN"}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {grn.status === "received" && (
          <div className="border border-[#EAE5D9] rounded-md p-3 flex flex-wrap items-center justify-between gap-2 bg-[#FDFBF7]" data-testid="grn-labels-bar">
            <div className="text-xs text-[#5C4033]">Stock received — print shelf / pack labels with barcode, MRP, MFG/EXP and ingredients.</div>
            <Button size="sm" variant="outline" onClick={() => setLabelsOpen(true)} data-testid="grn-print-labels-btn" className="border-[#C05621] text-[#C05621] hover:bg-[#FDF3EC]">
              <Printer className="w-4 h-4 mr-1" /> Print Labels
            </Button>
          </div>
        )}
        <GrnLabelPrintDialog grnId={grn.id} open={labelsOpen} onClose={() => setLabelsOpen(false)} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
